import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import {
  Sparkles, Lock, Copy, Share2, Palette,
  Zap, Paintbrush, Image as ImageIcon, Loader2, ArrowRight,
  QrCode, Check, LogIn, Upload, X, Edit3, BarChart3,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { QRPricingCards } from "@/components/pricing/QRPricingCards";

/* ── Style presets ──────────────────────────────────────────────────── */
const PRESETS = {
  plain:   { name: "Plain",       fg: "#000000", bg: "#FFFFFF", cost: 1 },
  scifi:   { name: "Afro Sci-Fi", fg: "#F2C94C", bg: "#0A0812", cost: 3 },
  custom:  { name: "Custom",      fg: "#F2C94C", bg: "#0A0812", cost: 3 },
};

/**
 * QRGenerator — public standalone page (no auth wall on preview).
 *
 * Download: HD, no-watermark via /api/qr/generate-hd (auth + credits).
 *   plain  1 cr  ·  Afro Sci-Fi  3 cr  ·  logo-embedded  5 cr
 *   Lifetime Pass holders get unlimited HD at $0. The old free watermarked
 *   tier was removed — users unlock HD or stay on the live preview.
 */
export default function QRGenerator() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const hasLifetimePass = !!user?.qr_lifetime_pass;
  const activeCostForUser = hasLifetimePass ? 0 : (null); // placeholder — real number computed below

  const [content, setContent] = useState("https://afrogeneo.com");
  const [styleId, setStyleId] = useState("scifi");
  const [customFg, setCustomFg] = useState("#F2C94C");
  const [customBg, setCustomBg] = useState("#0A0812");
  const [logoFile, setLogoFile] = useState(null);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [hdLoading, setHdLoading] = useState(false);
  const [buying, setBuying] = useState(null); // lookup_key being purchased, drives the two Stripe CTAs
  // Post-checkout state: shows the gold confirmation pill, a "GO TO DASHBOARD" CTA
  // for Dynamic buyers, and a manual fallback DOWNLOAD button if the browser
  // blocks the auto-trigger.
  const [paidKind, setPaidKind] = useState(null);         // "qr" | "dynamic" | null
  const [autoDlStatus, setAutoDlStatus] = useState("idle"); // idle | triggered | failed | done
  const [copied, setCopied] = useState(false);
  const [attachRef, setAttachRef] = useState(true);
  // Dynamic QR mode: when a signed-in user with an active subscription toggles this,
  // we mint a dynamic QR record and encode the /q/{short_code} URL instead of the
  // raw destination. The QR image itself becomes editable via /my-qr-codes.
  const [dynamicMode, setDynamicMode] = useState(false);
  const [dynamicQr, setDynamicQr] = useState(null); // {qr_id, short_url, short_code}
  const [dynamicBusy, setDynamicBusy] = useState(false);
  const dynamicUnlocked = !!user?.dynamic_qr_activated && user?.dynamic_qr_subscription_status === "active";

  const canvasRef = useRef(null);
  const logoInputRef = useRef(null);

  // Resolve current colors (custom style uses picker values)
  const { fg, bg } = useMemo(() => {
    if (styleId === "custom") return { fg: customFg, bg: customBg };
    return { fg: PRESETS[styleId].fg, bg: PRESETS[styleId].bg };
  }, [styleId, customFg, customBg]);

  // ── Referral auto-append ──────────────────────────────────────────
  // When a signed-in user encodes a URL, we transparently append `?ref={user_id}`
  // so every scan drops the visitor onto the app with the code stashed for signup.
  // Non-URL content (wifi, vcard, plain text) is left untouched.
  //
  // When dynamicMode is ON and we've minted a dynamic QR, the encoded target is
  // the short redirect URL (e.g. /q/ABC12345) — the referral is baked into the
  // stored destination on the server side, not the encoded URL. This keeps the
  // short URL clean and lets the user edit destinations later.
  const encodedContent = useMemo(() => {
    if (dynamicMode && dynamicQr?.short_url) {
      return dynamicQr.short_url;
    }
    const raw = (content || "").trim();
    if (!raw) return raw;
    if (!user?.user_id || !attachRef) return raw;
    try {
      const u = new URL(raw);
      if (!/^https?:$/.test(u.protocol)) return raw;
      if (u.searchParams.has("ref")) return raw; // respect existing ref
      u.searchParams.append("ref", user.user_id);
      return u.toString();
    } catch {
      return raw; // not a URL — skip
    }
  }, [content, user?.user_id, attachRef, dynamicMode, dynamicQr]);

  const refAttached = user?.user_id && attachRef && !dynamicMode && encodedContent !== (content || "").trim();

  // Mint a dynamic QR — turns the current content into a short editable redirect.
  const mintDynamicQr = useCallback(async () => {
    const raw = (content || "").trim();
    if (!raw) { toast.error("Enter a destination URL first."); return; }
    if (!/^https?:\/\//i.test(raw)) {
      toast.error("Dynamic QR needs an http(s):// URL as the destination.");
      return;
    }
    if (!user) { navigate("/?intent=signup"); return; }
    if (!dynamicUnlocked) { navigate("/my-qr-codes"); return; }
    setDynamicBusy(true);
    try {
      // Bake the referral into the destination stored server-side (not the short URL)
      let dest = raw;
      if (attachRef && user?.user_id) {
        try {
          const u = new URL(raw);
          if (/^https?:$/.test(u.protocol) && !u.searchParams.has("ref")) {
            u.searchParams.append("ref", user.user_id);
            dest = u.toString();
          }
        } catch {}
      }
      const { data } = await api.post("/qr/dynamic/create", { destination: dest });
      setDynamicQr({ qr_id: data.qr_id, short_url: data.short_url, short_code: data.short_code });
      setDynamicMode(true);
      toast.success("Dynamic QR minted — the code now points to an editable short URL.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "object" ? d.message : d;
      toast.error(msg || "Couldn't mint dynamic QR.");
      if (d?.code === "dynamic_qr_locked" || d?.code === "dynamic_qr_inactive") {
        navigate("/my-qr-codes");
      }
    } finally {
      setDynamicBusy(false);
    }
  }, [content, user, dynamicUnlocked, attachRef, navigate]);

  // ── Live render on any change ─────────────────────────────────────
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const text = (encodedContent || "").trim() || " ";
    QRCode.toCanvas(c, text, {
      errorCorrectionLevel: "H",
      margin: 2,
      width: 480,
      color: { dark: fg, light: bg },
    }).then(() => {
      // Overlay logo if provided (previewing centers a small logo on the QR)
      if (logoDataUrl) {
        const ctx = c.getContext("2d");
        const img = new Image();
        img.onload = () => {
          const edge = c.width * 0.22;
          const pad = edge * 0.14;
          const cx = (c.width - edge - pad * 2) / 2;
          const cy = (c.height - edge - pad * 2) / 2;
          // White rounded backdrop
          ctx.fillStyle = "#FFFFFF";
          roundRect(ctx, cx, cy, edge + pad * 2, edge + pad * 2, 12);
          ctx.fill();
          ctx.drawImage(img, cx + pad, cy + pad, edge, edge);
        };
        img.src = logoDataUrl;
      }
    }).catch(() => {});
  }, [encodedContent, fg, bg, logoDataUrl]);

  // ── Stripe checkout for the two preview CTAs ─────────────────────
  // `qr_lifetime_pass` → one-time $50 Custom QR Lifetime Pass
  // `dynamic_qr_activation` → $50 activation + $25/mo Dynamic QR + Analytics
  // Mirrors the logic in <QRPricingCards /> so the preview buttons and the
  // pricing section stay in lockstep. Unauthenticated users go through the
  // Emergent auth gate and come back to /qr-generator after sign-in.
  const startCheckout = async (lookup_key) => {
    if (!user) {
      toast.info("Sign in first — it's one tap.");
      const redirectUrl = window.location.origin + "/qr-generator";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    // Snapshot the user's current QR config so when Stripe redirects them back
    // we can restore it + auto-trigger the HD download without them re-entering
    // the same text/colors/logo. We only persist the raw inputs — the computed
    // preview re-renders deterministically from them.
    try {
      localStorage.setItem("geneo:qrPendingConfig", JSON.stringify({
        content, styleId, customFg, customBg,
        logoDataUrl: logoDataUrl || null,
        attachRef,
        savedAt: Date.now(),
      }));
    } catch {}
    setBuying(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBuying(null);
    }
  };

  // ── Post-checkout: restore config + auto-trigger HD download ────────
  // Flow: Stripe → /payment/success → polls Stripe → navigates to
  // /qr-generator?paid=qr|dynamic&session_id=... → we read localStorage,
  // restore the user's exact pre-checkout preview, refresh auth so the
  // lifetime-pass / dynamic-activated flag is visible, then auto-fire
  // the HD download after a 2-second grace so the user sees the gold
  // confirmation pill and the preview re-renders first.
  //
  // Browsers block programmatic downloads that aren't driven by a direct
  // user gesture. We render a prominent fallback button in the banner
  // UI unconditionally so the user is one click away even if the
  // auto-trigger is silently suppressed.
  //
  // Two-stage effect:
  //   stage 1 (mount) → parse URL, restore config, clean the URL
  //   stage 2 (user loaded) → fire the HD download
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("paid"); // "qr" | "dynamic" | null
    if (paid !== "qr" && paid !== "dynamic") return;

    // Restore the saved config so the preview matches what they bought.
    try {
      const raw = localStorage.getItem("geneo:qrPendingConfig");
      if (raw) {
        const cfg = JSON.parse(raw);
        if (cfg && Date.now() - (cfg.savedAt || 0) < 2 * 60 * 60 * 1000) {
          if (cfg.content != null) setContent(cfg.content);
          if (cfg.styleId) setStyleId(cfg.styleId);
          if (cfg.customFg) setCustomFg(cfg.customFg);
          if (cfg.customBg) setCustomBg(cfg.customBg);
          if (cfg.logoDataUrl !== undefined) setLogoDataUrl(cfg.logoDataUrl);
          if (typeof cfg.attachRef === "boolean") setAttachRef(cfg.attachRef);
        }
        localStorage.removeItem("geneo:qrPendingConfig");
      }
    } catch {}

    setPaidKind(paid);
    checkAuth?.();
    window.history.replaceState({}, "", "/qr-generator");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stage 2 — fire the HD download once the `user` object is hydrated.
  // Guarded by `autoDlStatus === "idle"` so it only runs once; subsequent
  // clicks on the fallback button go through the regular manual path.
  useEffect(() => {
    if (!paidKind || !user || autoDlStatus !== "idle") return;
    const t = setTimeout(async () => {
      setAutoDlStatus("triggered");
      try {
        await downloadHD();
        setAutoDlStatus("done");
      } catch {
        setAutoDlStatus("failed");
      }
    }, 2000);
    return () => clearTimeout(t);
  // downloadHD is referentially stable per render but we intentionally pin
  // this to paidKind + user transitions so the download fires exactly once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidKind, user?.user_id]);

  // ── HD download (auth + credits) ─────────────────────────────────
  const downloadHD = async () => {
    if (!user) {
      toast("Sign in to unlock HD downloads.", { icon: "🔒" });
      navigate("/?intent=qr-hd");
      return;
    }
    setHdLoading(true);
    try {
      const payload = {
        content: encodedContent.trim(),
        style: styleId,
        fg: styleId === "custom" ? customFg : (PRESETS[styleId].fg),
        bg: styleId === "custom" ? customBg : (PRESETS[styleId].bg),
        logo_url: logoDataUrl || null,
        size: 1024,
      };
      const { data } = await api.post("/qr/generate-hd", payload);
      // Force download by fetching the blob (crosses same-origin via our backend)
      const res = await fetch(data.image_url, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `geneo-qr-hd-${data.tier}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      toast.success(`HD downloaded · -${data.cost} credits`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402) {
        toast.error(typeof detail === "object" ? detail.message : "Not enough credits.");
        navigate("/pricing");
      } else {
        toast.error(typeof detail === "string" ? detail : "HD render failed.");
      }
    } finally {
      setHdLoading(false);
    }
  };

  // ── Share page URL ────────────────────────────────────────────────
  const share = useCallback(async () => {
    const url = `${window.location.origin}/qr-generator`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Geneo AI · QR Generator", url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch { /* user dismissed the share dialog — do nothing */ }
  }, []);

  // ── Logo upload ───────────────────────────────────────────────────
  const onLogoPick = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB."); return; }
    const reader = new FileReader();
    reader.onload = (e) => { setLogoDataUrl(e.target.result); setLogoFile(file); };
    reader.readAsDataURL(file);
  };

  const activeCost = logoDataUrl ? 5 : (styleId === "scifi" ? 3 : styleId === "custom" ? 3 : 1);

  return (
    <div className="relative min-h-screen bg-[#0a0812] text-white overflow-x-hidden">
      {/* ── Ambient background ─────────────────────────────────── */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 60% at 12% 18%, rgba(88,28,135,0.45), transparent 60%)," +
            "radial-gradient(50% 55% at 85% 78%, rgba(242,201,76,0.18), transparent 65%)," +
            "radial-gradient(45% 40% at 78% 12%, rgba(0,229,255,0.16), transparent 60%)," +
            "linear-gradient(180deg, #0a0812 0%, #0e0a1c 50%, #050408 100%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-14">
        {/* ── Header ──────────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-8 sm:mb-14">
          <button
            onClick={() => navigate("/")}
            data-testid="qr-home-btn"
            className="flex items-center gap-3 group"
          >
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-105"
              style={{
                background: "linear-gradient(135deg, #F2C94C 0%, #E27D60 50%, #7c3aed 100%)",
                boxShadow: "0 0 24px -4px rgba(242,201,76,0.55)",
              }}
            >
              <Sparkles className="w-5 h-5 text-[#0A0A0A]" />
            </div>
            <div className="text-left">
              <div className="font-display text-lg leading-none tracking-tight">GENEO AI</div>
              <div className="font-mono-hy text-[9px] tracking-[0.28em] text-[#F2C94C] mt-1">
                QR · GENERATOR
              </div>
            </div>
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={share}
              data-testid="qr-share-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/[0.04] hover:border-[#00E5FF] hover:bg-[#00E5FF]/[0.08] font-mono-hy text-[10px] tracking-[0.18em] text-[#00E5FF] transition-all"
              title="Copy this page link"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{copied ? "COPIED" : "SHARE"}</span>
            </button>
            {!user ? (
              <button
                onClick={() => navigate("/?intent=signup")}
                data-testid="qr-signin-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#F2C94C] hover:bg-[#F2D06B] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold hover:shadow-[0_0_24px_-4px_rgba(242,201,76,0.85)] transition-all"
              >
                <LogIn className="w-3.5 h-3.5" />
                SIGN IN
              </button>
            ) : (
              <button
                onClick={() => navigate("/studio")}
                data-testid="qr-studio-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#F2C94C]/40 bg-[#F2C94C]/[0.08] hover:bg-[#F2C94C]/[0.15] text-[#F2C94C] font-mono-hy text-[10px] tracking-[0.18em] transition-all"
              >
                STUDIO
              </button>
            )}
          </div>
        </div>

        {/* ── Hero ────────────────────────────────────────────── */}
        <div className="mb-10 sm:mb-14 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F2C94C]/10 border border-[#F2C94C]/30 mb-4">
            <QrCode className="w-3 h-3 text-[#F2C94C]" />
            <span className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.24em]">FREE · NO SIGN-UP</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-4">
            Design a QR that{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(120deg, #F2C94C 0%, #E27D60 45%, #00E5FF 100%)" }}
            >
              looks like the future
            </span>
          </h1>
          <p className="font-body text-base sm:text-lg text-white leading-relaxed max-w-xl">
            Generate stylish QR codes in seconds. Preview live, then unlock an HD no-watermark download with credits or a Lifetime Pass.
          </p>
        </div>

        {/* ── Two-column layout ───────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-10 items-start">

          {/* LEFT — Controls */}
          <div className="space-y-5">
            {/* Content input */}
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5">
              <label className="font-mono-hy text-[10px] text-white tracking-[0.24em] block mb-3">
                URL · TEXT · WI-FI · ANYTHING
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="https://afrogeneo.com"
                data-testid="qr-content-input"
                maxLength={2000}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-white/[0.08] bg-[#0A0A0A]/70 focus:border-[#F2C94C]/60 focus:outline-none focus:ring-2 focus:ring-[#F2C94C]/25 font-body text-[15px] text-white placeholder:text-white/50 resize-none"
              />
              <div className="mt-1 font-mono-hy text-[9px] text-white text-right tracking-widest">
                {content.length} / 2000
              </div>

              {/* Referral auto-append controller */}
              {user ? (
                refAttached ? (
                  <div
                    data-testid="qr-referral-attached"
                    className="mt-3 flex items-start gap-2 rounded-xl border border-[#27AE60]/35 bg-[#27AE60]/[0.08] px-3 py-2.5"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-[#27AE60] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-hy text-[9.5px] text-[#27AE60] tracking-[0.18em] mb-0.5">
                        REFERRAL LINK ATTACHED · +10 CR PER SIGNUP
                      </div>
                      <div className="font-mono text-[10px] text-white/75 truncate" title={encodedContent}>
                        {encodedContent}
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid="qr-referral-toggle-off"
                      onClick={() => setAttachRef(false)}
                      className="font-mono-hy text-[9px] tracking-[0.18em] text-white/70 hover:text-white underline underline-offset-2 flex-shrink-0"
                      title="Turn off auto-append for this session"
                    >
                      OFF
                    </button>
                  </div>
                ) : (
                  content && !attachRef && (
                    <button
                      type="button"
                      data-testid="qr-referral-toggle-on"
                      onClick={() => setAttachRef(true)}
                      className="mt-3 w-full text-left rounded-xl border border-white/[0.08] hover:border-[#F2C94C]/40 bg-white/[0.02] px-3 py-2 font-mono-hy text-[9.5px] tracking-[0.18em] text-white/60 hover:text-[#F2C94C] transition-colors"
                    >
                      + ATTACH MY REFERRAL LINK · EARN +10 CR PER SIGNUP
                    </button>
                  )
                )
              ) : (
                <div
                  data-testid="qr-referral-signin-cta"
                  className="mt-3 flex items-center gap-2 rounded-xl border border-[#F2C94C]/25 bg-[#F2C94C]/[0.05] px-3 py-2"
                >
                  <Lock className="w-3 h-3 text-[#F2C94C] flex-shrink-0" />
                  <span className="font-mono-hy text-[9px] text-white/80 tracking-[0.14em]">
                    <button
                      onClick={() => navigate("/?intent=signup")}
                      className="text-[#F2C94C] hover:underline"
                    >
                      SIGN IN
                    </button>{" "}
                    TO EARN +10 CR EACH TIME SOMEONE SCANS YOUR CODE
                  </span>
                </div>
              )}

              {/* ── Dynamic QR toggle ────────────────────────────── */}
              {dynamicMode && dynamicQr ? (
                <div
                  data-testid="qr-dynamic-active"
                  className="mt-3 rounded-xl border border-[#00E5FF]/35 bg-[#00E5FF]/[0.08] px-3 py-2.5"
                >
                  <div className="flex items-start gap-2 mb-2">
                    <Sparkles className="w-3.5 h-3.5 text-[#00E5FF] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-hy text-[9.5px] text-[#00E5FF] tracking-[0.18em] mb-0.5">
                        DYNAMIC QR · EDITABLE · TRACKED
                      </div>
                      <div className="font-mono text-[10px] text-white/80 truncate" title={dynamicQr.short_url}>
                        {dynamicQr.short_url}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button
                      onClick={() => navigate("/my-qr-codes")}
                      data-testid="qr-dynamic-dashboard"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#00E5FF]/40 hover:border-[#00E5FF] hover:bg-[#00E5FF]/[0.08] font-mono-hy text-[9px] tracking-[0.18em] text-[#00E5FF] transition-all"
                    >
                      <BarChart3 className="w-3 h-3" /> ANALYTICS
                    </button>
                    <button
                      onClick={() => navigate("/my-qr-codes")}
                      data-testid="qr-dynamic-edit"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#F2C94C]/40 hover:border-[#F2C94C] hover:bg-[#F2C94C]/[0.08] font-mono-hy text-[9px] tracking-[0.18em] text-[#F2C94C] transition-all"
                    >
                      <Edit3 className="w-3 h-3" /> EDIT
                    </button>
                    <button
                      onClick={() => { setDynamicMode(false); setDynamicQr(null); }}
                      data-testid="qr-dynamic-off"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/[0.15] hover:border-white/40 font-mono-hy text-[9px] tracking-[0.18em] text-white/70 transition-all"
                    >
                      REVERT TO STATIC
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={dynamicUnlocked ? mintDynamicQr : () => navigate(user ? "/my-qr-codes" : "/?intent=signup")}
                  disabled={dynamicBusy || !content.trim()}
                  data-testid="qr-dynamic-toggle"
                  className={`mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border transition-all font-mono-hy text-[9.5px] tracking-[0.18em] disabled:opacity-40 disabled:cursor-not-allowed ${
                    dynamicUnlocked
                      ? "border-[#00E5FF]/25 hover:border-[#00E5FF]/60 bg-[#00E5FF]/[0.03] hover:bg-[#00E5FF]/[0.08] text-[#00E5FF]"
                      : "border-white/[0.08] hover:border-[#00E5FF]/40 bg-white/[0.02] text-white/70 hover:text-[#00E5FF]"
                  }`}
                >
                  {dynamicBusy ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> MINTING DYNAMIC QR…</>
                  ) : dynamicUnlocked ? (
                    <><Sparkles className="w-3.5 h-3.5" /> MAKE IT DYNAMIC · EDIT ANY TIME + TRACK SCANS</>
                  ) : (
                    <><Lock className="w-3.5 h-3.5" /> UPGRADE TO DYNAMIC · EDIT ANY TIME + SCAN ANALYTICS</>
                  )}
                </button>
              )}
            </div>

            {/* Style presets */}
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5">
              <label className="font-mono-hy text-[10px] text-white tracking-[0.24em] block mb-3">
                STYLE
              </label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(PRESETS).map(([id, p]) => {
                  const active = styleId === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setStyleId(id)}
                      data-testid={`qr-style-${id}`}
                      className={`relative rounded-xl border p-3 flex flex-col items-center gap-2 transition-all ${
                        active
                          ? "border-[#F2C94C] bg-[#F2C94C]/[0.08] shadow-[0_0_20px_-6px_rgba(242,201,76,0.55)]"
                          : "border-white/[0.08] bg-white/[0.02] hover:border-[#F2C94C]/40"
                      }`}
                    >
                      <div
                        className="w-9 h-9 rounded-lg border border-white/[0.08]"
                        style={{ background: `linear-gradient(135deg, ${p.fg} 50%, ${p.bg} 50%)` }}
                      />
                      <div className="font-mono-hy text-[9px] tracking-[0.14em] text-white">
                        {p.name}
                      </div>
                      <div className="font-mono-hy text-[8px] text-[#F2C94C]/80 tracking-widest">
                        {p.cost} CR HD
                      </div>
                    </button>
                  );
                })}
              </div>

              {styleId === "custom" && (
                <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-white/[0.06]" data-testid="qr-custom-colors">
                  <label className="block">
                    <span className="font-mono-hy text-[9px] text-white tracking-widest">FOREGROUND</span>
                    <div className="mt-1.5 flex items-center gap-2 px-3 py-2 rounded-xl border border-white/[0.08] bg-[#0A0A0A]/60">
                      <input
                        type="color"
                        value={customFg}
                        onChange={(e) => setCustomFg(e.target.value)}
                        data-testid="qr-color-fg"
                        className="w-8 h-8 rounded cursor-pointer bg-transparent"
                      />
                      <span className="font-mono-hy text-[11px] text-white">{customFg}</span>
                    </div>
                  </label>
                  <label className="block">
                    <span className="font-mono-hy text-[9px] text-white tracking-widest">BACKGROUND</span>
                    <div className="mt-1.5 flex items-center gap-2 px-3 py-2 rounded-xl border border-white/[0.08] bg-[#0A0A0A]/60">
                      <input
                        type="color"
                        value={customBg}
                        onChange={(e) => setCustomBg(e.target.value)}
                        data-testid="qr-color-bg"
                        className="w-8 h-8 rounded cursor-pointer bg-transparent"
                      />
                      <span className="font-mono-hy text-[11px] text-white">{customBg}</span>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {/* Logo upload */}
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5">
              <label className="font-mono-hy text-[10px] text-white tracking-[0.24em] block mb-3">
                LOGO · OPTIONAL · +5 CR
              </label>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => onLogoPick(e.target.files?.[0])}
                className="hidden"
                data-testid="qr-logo-file"
              />
              {logoDataUrl ? (
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl overflow-hidden border border-[#F2C94C]/40 bg-white flex-shrink-0">
                    <img src={logoDataUrl} alt="Logo" className="w-full h-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-hy text-[10px] text-[#F2C94C] tracking-widest truncate">
                      {logoFile?.name || "logo.png"}
                    </div>
                    <div className="font-mono-hy text-[9px] text-white tracking-widest">
                      EMBEDDED CENTER
                    </div>
                  </div>
                  <button
                    onClick={() => { setLogoDataUrl(null); setLogoFile(null); }}
                    data-testid="qr-logo-remove"
                    className="w-8 h-8 rounded-full border border-[#EB5757]/40 text-[#EB5757] hover:bg-[#EB5757]/10 flex items-center justify-center"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => logoInputRef.current?.click()}
                  data-testid="qr-logo-pick"
                  className="w-full rounded-xl border-2 border-dashed border-[#F2C94C]/25 bg-[#F2C94C]/[0.02] hover:border-[#F2C94C]/60 hover:bg-[#F2C94C]/[0.05] py-4 flex flex-col items-center gap-1.5 transition-all"
                >
                  <Upload className="w-4 h-4 text-[#F2C94C]/70" />
                  <span className="font-mono-hy text-[10px] text-white tracking-widest">
                    UPLOAD LOGO
                  </span>
                  <span className="font-mono-hy text-[9px] text-white/70 tracking-widest">
                    PNG · JPG · SVG · under 2 MB
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* RIGHT — Preview + downloads */}
          <div className="space-y-5 lg:sticky lg:top-6">
            <div
              className="rounded-3xl border border-[#F2C94C]/25 shadow-[0_0_40px_-12px_rgba(242,201,76,0.45)] p-6 flex flex-col items-center gap-4"
              style={{ background: "linear-gradient(160deg, #120E1F 0%, #0A0812 100%)" }}
            >
              <div className="w-full flex items-center justify-between">
                <div className="font-mono-hy text-[10px] text-[#F2C94C] tracking-[0.24em]">
                  LIVE PREVIEW
                </div>
                <div className="font-mono-hy text-[9px] text-white tracking-widest">
                  {hasLifetimePass ? "LIFETIME PASS · HD FREE" : `${activeCost} CR · HD`}
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden p-3" style={{ background: bg }}>
                <canvas ref={canvasRef} data-testid="qr-preview-canvas" width={480} height={480} className="block max-w-full h-auto" />
              </div>
            </div>

            {/* Post-checkout confirmation banner — auto-triggers HD download in 2s,
               with a manual fallback below in case the browser blocks the auto-click.
               Dynamic buyers also see a "GO TO YOUR DASHBOARD" cyan CTA. */}
            {paidKind && (
              <div data-testid="qr-paid-banner" className="space-y-3">
                <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-gradient-to-r from-[#F2C94C]/18 via-[#F2C94C]/10 to-transparent border border-[#F2C94C]/40 shadow-[0_0_28px_-8px_rgba(242,201,76,0.55)]">
                  <Check className="w-4 h-4 text-[#F2C94C]" strokeWidth={3} />
                  <span className="font-mono-hy text-[10px] tracking-[0.18em] text-[#F2C94C]">
                    {autoDlStatus === "done"
                      ? "QR CODE DOWNLOADED — ENJOY"
                      : "PAYMENT CONFIRMED — YOUR QR CODE IS DOWNLOADING"}
                  </span>
                </div>
                <div className={`grid grid-cols-1 ${paidKind === "dynamic" ? "sm:grid-cols-2" : ""} gap-3`}>
                  <button
                    onClick={downloadHD}
                    disabled={hdLoading}
                    data-testid="qr-paid-fallback-download"
                    className="inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl bg-[#F2C94C] hover:bg-[#F2D06B] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.2em] font-bold hover:shadow-[0_0_28px_-4px_rgba(242,201,76,0.85)] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                  >
                    {hdLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> DOWNLOADING…</>
                    ) : (
                      <><Zap className="w-4 h-4" /> DOWNLOAD YOUR QR CODE</>
                    )}
                  </button>
                  {paidKind === "dynamic" && (
                    <button
                      onClick={() => navigate("/my-qr-codes")}
                      data-testid="qr-paid-dashboard"
                      className="inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl bg-transparent border-2 border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 font-mono-hy text-[10px] tracking-[0.2em] font-bold hover:shadow-[0_0_28px_-4px_rgba(0,229,255,0.75)] transition-all"
                    >
                      <BarChart3 className="w-4 h-4" /> GO TO YOUR DASHBOARD
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Payment CTAs — two side-by-side buttons replacing the old HD-credit flow.
               Gold = one-time Custom QR Lifetime Pass ($50). Cyan = Dynamic QR activation ($50 + $25/mo).
               Lifetime-pass holders get a direct HD download instead; dynamic-active users jump to /my-qr.
               Hidden entirely during the post-checkout success state so the user focuses on the download. */}
            {!paidKind && (hasLifetimePass ? (
              <div className="grid grid-cols-1">
                <button
                  onClick={downloadHD}
                  disabled={hdLoading}
                  data-testid="qr-download-hd"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl bg-[#F2C94C] hover:bg-[#F2D06B] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.2em] font-bold hover:shadow-[0_0_28px_-4px_rgba(242,201,76,0.85)] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  {hdLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> RENDERING…</>
                             : <><Zap className="w-4 h-4" /> HD · UNLOCKED</>}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* ── Gold: Custom QR · $50 one-time ─────────────────────── */}
                <button
                  onClick={() => startCheckout("qr_lifetime_pass")}
                  disabled={buying !== null}
                  data-testid="qr-buy-lifetime-btn"
                  className="inline-flex flex-col items-center justify-center gap-1 px-4 py-3.5 rounded-2xl bg-[#F2C94C] hover:bg-[#F2D06B] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.2em] font-bold hover:shadow-[0_0_28px_-4px_rgba(242,201,76,0.85)] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  {buying === "qr_lifetime_pass" ? (
                    <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> REDIRECTING…</span>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-2"><Zap className="w-4 h-4" /> CUSTOM QR · $50</span>
                      <span className="font-mono-hy text-[8px] tracking-widest opacity-80">LIFETIME · UNLIMITED HD</span>
                    </>
                  )}
                </button>

                {/* ── Cyan: Dynamic QR · $50 + $25/mo subscription ───────── */}
                <button
                  onClick={() => {
                    // Dynamic-QR already active? Skip checkout and send them to the manage page.
                    if (user?.dynamic_qr_activated && user?.dynamic_qr_subscription_status === "active") {
                      navigate("/my-qr");
                      return;
                    }
                    startCheckout("dynamic_qr_activation");
                  }}
                  disabled={buying !== null}
                  data-testid="qr-buy-dynamic-btn"
                  className="inline-flex flex-col items-center justify-center gap-1 px-4 py-3.5 rounded-2xl bg-transparent border-2 border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 font-mono-hy text-[10px] tracking-[0.2em] font-bold hover:shadow-[0_0_28px_-4px_rgba(0,229,255,0.75)] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  {buying === "dynamic_qr_activation" ? (
                    <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> REDIRECTING…</span>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-2"><BarChart3 className="w-4 h-4" /> DYNAMIC · $50 + $25/MO</span>
                      <span className="font-mono-hy text-[8px] tracking-widest opacity-80">EDITABLE LINK · SCAN ANALYTICS</span>
                    </>
                  )}
                </button>
              </div>
            ))}

            {/* Pricing hint */}
            {hasLifetimePass ? (
              <div className="text-center font-mono-hy text-[9px] text-[#00E5FF] tracking-widest" data-testid="qr-pass-badge">
                LIFETIME PASS ACTIVE · UNLIMITED HD · NO WATERMARK
              </div>
            ) : (
              <div className="text-center font-mono-hy text-[9px] text-white tracking-widest">
                PLAIN · 1 CR &nbsp;·&nbsp; AFRO SCI-FI · 3 CR &nbsp;·&nbsp; LOGO · 5 CR &nbsp;·&nbsp;{" "}
                <button onClick={() => navigate("/pricing")} className="text-[#F2C94C] hover:underline" data-testid="qr-pass-upsell">
                  UNLOCK LIFETIME · $50
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── CTA banner ─────────────────────────────────────── */}
        <div
          className="mt-14 sm:mt-20 rounded-3xl border border-[#F2C94C]/25 overflow-hidden relative"
          style={{
            background:
              "radial-gradient(80% 100% at 0% 100%, rgba(88,28,135,0.5), transparent 60%)," +
              "radial-gradient(80% 100% at 100% 0%, rgba(242,201,76,0.15), transparent 55%)," +
              "linear-gradient(120deg, #0e0a1c 0%, #120E1F 100%)",
          }}
          data-testid="qr-cta-banner"
        >
          <div className="relative px-5 sm:px-10 py-8 sm:py-12 flex flex-col lg:flex-row items-center gap-6">
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 mb-3">
                <Sparkles className="w-3 h-3 text-[#00E5FF]" />
                <span className="font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.24em]">GENEO AI · FULL STUDIO</span>
              </div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight tracking-tight mb-2">
                Bring{" "}
                <span
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: "linear-gradient(120deg, #F2C94C 0%, #E27D60 45%, #00E5FF 100%)" }}
                >
                  Afro Sci-Fi
                </span>{" "}
                worlds to life
              </h2>
              <p className="font-body text-sm sm:text-base text-white leading-relaxed max-w-lg">
                QR is just the appetizer. Generate cinematic Afro Sci-Fi imagery, animate videos, clone voices, and build entire storyboards inside Geneo AI Studio.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
              <button
                onClick={() => navigate(user ? "/studio" : "/?intent=signup")}
                data-testid="qr-cta-signup"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-[#F2C94C] hover:bg-[#F2D06B] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold hover:shadow-[0_0_36px_-4px_rgba(242,201,76,0.85)] transition-all whitespace-nowrap"
              >
                {user ? "OPEN STUDIO" : "SIGN UP · FREE"}
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate("/pricing")}
                data-testid="qr-cta-pricing"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl border border-[#00E5FF]/40 hover:border-[#00E5FF] hover:bg-[#00E5FF]/[0.08] font-mono-hy text-[10px] tracking-[0.22em] text-[#00E5FF] transition-all whitespace-nowrap"
              >
                SEE PRICING
              </button>
            </div>
          </div>
        </div>

        {/* ── Pricing (Custom QR + Dynamic QR) ───────────────── */}
        <div className="mt-14 sm:mt-20" data-testid="qr-pricing-block">
          <QRPricingCards />
        </div>

        {/* Foot */}
        <div className="mt-10 text-center font-mono-hy text-[9px] text-white/60 tracking-[0.2em]">
          MADE WITH · GENEO AI · AFRO SCI-FI
        </div>
      </div>
    </div>
  );
}

/* ── Helpers ──────────────────────────────────────────────────────── */
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}
